import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { TableCell } from "@/shared/components/ui/table";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Eye,
  RefreshCw,
  XCircle,
  Copy,
  Check,
} from "lucide-react";
import { TransactionTypeIcon } from "./TransactionTypeIcon";
import { TransactionStatusBadge } from "./TransactionStatusBadge";
import {
  formatAmountWithCurrency,
  getAmount,
  getFeeAmount,
  getNetAmount,
} from "./utils";

export const TransactionRow = React.memo(
  ({
    tx,
    index,
    onView,
    onRetry,
    onCancel,

    showReseller = true,
    showMerchant = true,
    showProvider = true,
    t,
    translate,
  }) => {
    const [isCopied, setIsCopied] = React.useState(false);
    const isWithdrawal = String(tx.type || "").toUpperCase() === "WITHDRAWAL";
    const displayId = tx.providerTransactionId || tx.id;

    const handleCopy = (e) => {
      e.stopPropagation();
      if (!displayId) return;
      navigator.clipboard.writeText(displayId);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    };

    const cells = [
      {
        id: "transaction",
        className: "py-3",
        content: (
          <div className="flex items-center gap-3">
            <TransactionTypeIcon isWithdrawal={isWithdrawal} size="h-4 w-4" />
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="font-mono text-[11px] font-medium text-slate-500 truncate block max-w-[100px]">
                  {displayId?.slice(0, 12) || displayId || "—"}
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`p-1 rounded-md transition-all ${isCopied
                      ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                      : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    }`}
                >
                  {isCopied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">
                {isWithdrawal
                  ? t
                    ? t("withdrawal")
                    : "Withdrawal"
                  : t
                    ? t("deposit")
                    : "Deposit"}
              </p>
            </div>
          </div>
        ),
      },
      showReseller && {
        id: "reseller",
        className: "py-3 hidden md:table-cell",
        content: tx.reseller?.name ? (
          <span className="font-medium text-sm text-slate-700 truncate block max-w-[120px]">
            {tx.reseller.name}
          </span>
        ) : (
          <span className="text-xs text-slate-400 font-medium">
            {translate("direct")}
          </span>
        ),
      },
      showMerchant && {
        id: "merchant",
        className: "py-3",
        content: (
          <span className="text-sm font-medium text-slate-700 truncate block max-w-[120px]">
            {tx.merchant?.name || "—"}
          </span>
        ),
      },
      showProvider && {
        id: "provider",
        className: "py-3 hidden lg:table-cell",
        content: (
          <span className="text-sm text-slate-600 font-medium">
            {isWithdrawal
              ? tx.processorBankcode || "—"
              : tx.providerCode || "—"}
          </span>
        ),
      },
      {
        id: "amount",
        className: "py-3 text-center",
        content: (
          <span className="font-bold text-slate-900 text-sm sm:text-base">
            {formatAmountWithCurrency(getAmount(tx), tx.currency)}
          </span>
        ),
      },
      {
        id: "fee",
        className: "py-3 text-center",
        content: (
          <span className="font-medium text-red-500 text-sm sm:text-base">
            {formatAmountWithCurrency(getFeeAmount(tx), tx.currency)}
          </span>
        ),
      },
      {
        id: "net",
        className: "py-3 text-center",
        content: (
          <span className="font-bold text-emerald-600 text-sm sm:text-base">
            {formatAmountWithCurrency(getNetAmount(tx), tx.currency)}
          </span>
        ),
      },
      {
        id: "currency",
        className: "py-3 text-center",
        content: (
          <Badge
            variant="outline"
            className="text-[10px] font-bold uppercase border-slate-200 text-slate-500 bg-slate-50/50"
          >
            {tx.currency || "PKR"}
          </Badge>
        ),
      },
      {
        id: "status",
        className: "py-3",
        content: <TransactionStatusBadge status={tx.status} t={t} />,
      },
      {
        id: "date",
        className: "py-3 hidden sm:table-cell",
        content: (
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-slate-700 whitespace-nowrap">
              {tx.createdAt
                ? format(
                  new Date(tx.createdAt),
                  "MMM d, yyyy",
                )
                : "—"}
            </span>
          </div>
        ),
      },
      {
        id: "actions",
        className: "py-3 text-right",
        content: (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onView?.(tx);
                }}
                className="cursor-pointer"
              >
                <Eye className="h-4 w-4 mr-2 text-slate-400" />
                {translate("viewDetails")}
              </DropdownMenuItem>
              {(tx.status === "failed" || tx.status === "expired") && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry?.(tx);
                  }}
                  className="cursor-pointer"
                >
                  <RefreshCw className="h-4 w-4 mr-2 text-blue-500" />
                  {translate("retry")}
                </DropdownMenuItem>
              )}
              {tx.status === "pending" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel?.(tx);
                  }}
                  className="text-red-600 cursor-pointer"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {translate("cancel")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ].filter(Boolean);

    return (
      <motion.tr
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.02 * index }}
        className="hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0"
        onClick={() => onView?.(tx)}
      >
        {cells.map((cell) => (
          <TableCell key={cell.id} className={cell.className}>
            {cell.content}
          </TableCell>
        ))}
      </motion.tr>
    );
  },
);

